import React, {useEffect, useState} from 'react';
import {Col, Container, Row} from 'reactstrap';
import queryString from 'query-string';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState, usePhaseDispatch} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import HeaderVideo from 'components/Elements/HeaderVideo';
import CheckoutButton from 'components/CheckoutButton';
import CountdownTimer from 'components/Elements/CountdownTimer';
import {SalesDisclaimer} from 'components/SalesCta';
import SalesCopy from 'components/SalesCopy';
import Footer from 'components/Elements/Footer';
import ExitModal from 'components/ComponentLibrary/ExitModal';

export default function SalesIndex({
  noExitIntent = false,
  noFixedVideo = true,
  ctaOverride = null,
  timerOverride = null,
  videoUrlOverride = null,
  videoThumbnailOverride = null,
  ctaNoTimer = false,
  variant = null,
  ...props
}) {
  const {bucket, email, registrationId} = useUserState();
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before',
        launchStart: '',
        launchEnd: '',
        launchTitle: ''
      },
      sales: salesSchedule = {
        phase: 'during',
        salesEnd: '',
        isSalesDay: false
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const qs = typeof window !== 'undefined' ? queryString.parse(window.location.search) : {};
  const {t} = qs.t ? qs : {};

  const pageTitle = 'Join Tackling Type 2!';
  const pageDescription = 'Get everything you need to stay healthy inside Tackling Type 2.';
  const ogImage = 'https://cdn.foodrevolution.org/tt2/og/tt2-evergreen-opengraph-sales-1200x1200-v20220804.png';
  const shareUrl = 'https://type2.foodrevolution.org/join/';

  const isCartClose = new Date(webinarSchedule.endOfSalesDay * 1000 - 86400000).getTime() < new Date().getTime();
  const salesEnd = new Date(webinarSchedule.endOfSalesDay * 1000).getTime();
  const salesTimerDate = timerOverride ? timerOverride : salesEnd;

  const salesFullPrice =
    (!email && !registrationId && !ctaOverride) ||
    (!ctaOverride && webinarSchedule.phase === 'after') ||
    ctaOverride === 'sales497';

  const productID = 'C-TT2';
  const productSku = 'C-TT2';
  const productName = 'Tackling Type 2';
  const productSlug = t || !salesFullPrice ? 'tt2-special' : 'tt2';
  const productValue = t || !salesFullPrice ? 297 : 497;
  const paymentPlanPrice = t || !salesFullPrice ? 109 : 179;

  useEffect(() => {
    track('Product Viewed', {
      product_id: productID,
      sku: productSku,
      name: productName,
      variant: productSlug,
      value: productValue,
      currency: 'usd'
    });
  }, [salesFullPrice]);

  const [videoControl, setvideoControl] = useState(props.videoControl || 'control');
  useEffect(() => {
    subscribeToCampaign((variantIndex) => {
      switch (variantIndex) {
        case 1:
          setvideoControl('v1');
          break;
      }
    }, 1325);
  }, []);

  const cartCloseVideo = {
    prevent: 'https://www.youtube.com/watch?v=zTgwCdiMOCc',
    optimize: 'https://www.youtube.com/watch?v=zTgwCdiMOCc',
    reverse: 'https://www.youtube.com/watch?v=YptfECNd08k'
  }[bucket];
  const cartCloseVideoThumb = 'https://cdn.foodrevolution.org/tt2/videothumb/1154221199-thumb.jpg';

  const salesPriceVideo = {
    prevent: 'https://www.youtube.com/watch?v=h5G2J45mo-o',
    optimize: 'https://www.youtube.com/watch?v=h5G2J45mo-o',
    reverse: 'https://www.youtube.com/watch?v=8jqLV4Z73sA'
  }[bucket];
  const salesPriceVideoThumb = 'https://cdn.foodrevolution.org/tt2/videothumb/1357522236-thumb.jpg';

  const fullPriceVideo = {
    prevent: 'https://www.youtube.com/watch?v=bpOGfDYeDho',
    optimize: 'https://www.youtube.com/watch?v=bpOGfDYeDho',
    reverse: 'https://www.youtube.com/watch?v=v-2JrwMJ6vk'
  }[bucket];
  const fullPriceVideoThumb = 'https://cdn.foodrevolution.org/tt2/videothumb/1789299492-thumb.jpg';

  const videoUrl = {
    control: videoUrlOverride
      ? videoUrlOverride
      : t || !salesFullPrice
      ? isCartClose
        ? cartCloseVideo
        : salesPriceVideo
      : fullPriceVideo,
    v1: 'https://www.youtube.com/watch?v=w_9JctbxVcs'
  }[videoControl];
  const videoThumbnail = {
    control: videoThumbnailOverride
      ? videoThumbnailOverride
      : t || !salesFullPrice
      ? isCartClose
        ? cartCloseVideoThumb
        : salesPriceVideoThumb
      : fullPriceVideoThumb,
    v1: 'https://i.ytimg.com/vi/w_9JctbxVcs/maxresdefault.jpg'
  }[videoControl];

  useBeacon();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title={pageTitle} description={pageDescription} url={shareUrl} ogImage={ogImage} />

      <div className="page-content">
        <SectionCL id="join-header-cta" color="black" className="text-center text-white" order="first">
          <Container>
            <Row className="align-items-center">
              <Col lg="6" className="mb-4 mb-lg-0">
                <h2 className="text-h5 mt-0 mb-2">The critical question:</h2>
                <h1 className="text-h3 mt-0">
                  {
                    {
                      prevent: <>Are you sure you’re doing everything you can to prevent diabetes?</>,
                      optimize: <>Are you sure you’re doing everything you can to reverse prediabetes?</>,
                      reverse: <>Are you sure you’re doing everything you can to reverse diabetes?</>
                    }[bucket]
                  }
                </h1>
                {
                  {
                    control: <p className="text-800 mb-0">Watch this video to learn the truth.</p>,
                    v1: (
                      <p className="text-800 mb-0">
                        Watch the first video from the Tackling Type 2 course where Ocean walks through exactly what's
                        included and how to use it.
                      </p>
                    )
                  }[videoControl]
                }
              </Col>
              <Col lg="6" className="px-0 px-sm-3">
                <HeaderVideo
                  url={videoUrl}
                  thumbnail={videoThumbnail}
                  noFixed={noFixedVideo}
                  sku={productSku}
                  slug={productSlug}
                  price={productValue}
                  label="TT2 - Sales"
                  sales
                />
              </Col>
            </Row>
          </Container>
        </SectionCL>
        <SectionCL id="" color="white" className="text-center" angle="none" innerClass="sectionCL-padding-b60">
          <Container>
            <Row>
              <Col>
                <h4 className="section-heading">Get immediate access to the groundbreaking program</h4>
                <img className="img-course-logo mb-4" src="https://cdn.foodrevolution.org/tt2/tt2-logo-2024.svg" />
                <p>Brought to You By Brenda Davis, RD, and the Team at Food Revolution Network</p>
                <div className="header-cta-wrap mt-5">
                  {t || !salesFullPrice ? (
                    <>
                      <h2 className="section-heading mb-3">
                        Right Now Just <del>$497</del> <span className="text-green">$297</span>
                      </h2>
                      {!ctaNoTimer ? (
                        <>
                          <h5 className="mt-4">Your discount expires in:</h5>
                          <CountdownTimer time={salesTimerDate} className="countdown-dark" />
                        </>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <h2 className="section-heading">
                        Right Now <span className="text-green">$497</span>
                      </h2>
                    </>
                  )}
                  <CheckoutButton sku={productSku} price={productValue} slug={productSlug} className="my-3" />
                  <SalesDisclaimer />
                </div>
              </Col>
            </Row>
          </Container>
        </SectionCL>

        <SalesCopy
          version="sales"
          productId={productID}
          productSku={productSku}
          productSlug={productSlug}
          productPrice={productValue}
          paymentPlanPrice={paymentPlanPrice}
          variant={variant}
          salesFullPrice={salesFullPrice}
          ctaOverride={ctaOverride}
          ctaNoTimer={ctaNoTimer}
          timerOverride={timerOverride}
        />
      </div>
      {!noExitIntent && (
        <ExitModal
          exitDelay={30000}
          content={
            <>
              <h4 className="section-heading mb-4">Before you go…</h4>
              {t || !salesFullPrice ? (
                <>
                  <p>
                    This is your chance to get <br className="d-none d-md-block" />
                    <i>Tackling Type 2</i> for 40% off.
                  </p>
                  <p>Get it before the price goes up…</p>
                  <p className="mb-0">
                    Enjoy all the diabetes-busting recipes, <br className="d-none d-md-block" />
                    life-changing lessons, community, and <br className="d-none d-md-block" />
                    ongoing support for years to come!
                  </p>
                </>
              ) : (
                <>
                  <p>
                    You’re about to miss getting <br className="d-none d-lg-block d-xl-none" />
                    all the <br className="d-none d-xl-block" />
                    diabetes-busting recipes, <br className="d-none d-lg-block d-xl-none" />
                    life-changing <br className="d-none d-xl-block" />
                    lessons, community, and ongoing support <br className="d-none d-xl-block" />
                    inside <br className="d-none d-lg-block d-xl-none" />
                    <i>Tackling Type 2.</i>
                  </p>
                  <p className="mb-0">
                    It’s not too late to change your mind and get everything you need to be your healthiest!
                  </p>
                </>
              )}
            </>
          }
          image={{
            url: 'https://cdn.foodrevolution.org/tt2/tt2-product-image-v20240207.png',
            alt: '',
            width: 1500,
            height: 711
          }}
          imageBackground={false}
          positiveButtonText={'Yes, I want to protect my health'}
          positiveButtonColor="exit-orange"
          negativeButtonText={'No thanks, I’ll take my chances'}
          scrollHref={'course-cta-1'}
          scrollOffset={0}
        />
      )}

      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
